<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!--     Search + category filter     -->
    <div v-if="services.length > 0" class="flex-shrink-0 px-4 pt-2 pb-2 bg-white">
      <div class="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-3">
        <input
          v-model="query"
          placeholder="חיפוש שירות"
          class="flex-1 bg-transparent outline-none text-sm"
        />
        <i class="pi pi-search text-gray-400 text-sm" />
      </div>
      <div v-if="categories.length > 1" class="flex gap-2 overflow-x-auto pb-1 mt-3">
        <button
          v-for="c in categories"
          :key="c"
          type="button"
          class="px-4 py-1.5 rounded-full text-sm whitespace-nowrap border transition-colors flex-none"
          :class="cat === c ? 'bg-primary text-white border-transparent font-medium' : 'bg-white text-gray-600 border-gray-200'"
          @click="cat = c"
        >{{ c === ALL ? 'הכול' : c }}</button>
      </div>
    </div>

    <div class="flex-1 flex flex-col min-h-0 overflow-y-auto">
      <!--     Loading     -->
      <div v-if="!adminStore.getBusinessSiteData" class="flex flex-col">
        <div v-for="i in [1,2,3]"
             class="w-full py-4 border-b flex gap-3 justify-start items-center border-gray-200 mx-2">
          <Skeleton size="3rem" />
          <div class="flex flex-grow">
            <div class="flex flex-col gap-2 items-start justify-start">
              <Skeleton borderRadius="12px" width="8rem" height="0.75rem" />
              <Skeleton borderRadius="12px" width="8rem" height="0.5rem" />
            </div>
            <RadioButton inputId="cheese" value="Cheese" class="mr-auto" />
          </div>
        </div>
      </div>
      <!--     Empty — no services at all     -->
      <div v-else-if="services.length === 0"
           class="h-full flex flex-col items-center justify-center text-center px-6 py-12 gap-2">
        <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-1">
          <i class="pi pi-tag text-2xl text-gray-400" />
        </div>
        <span class="font-medium">עדיין אין שירותים</span>
        <span class="text-sm text-gray-500 max-w-[16rem]">כשתוסיפו שירותים לעסק, הם יופיעו כאן לבחירה.</span>
      </div>
      <!--     Empty — no matches for the current search / filter     -->
      <div v-else-if="filteredServices.length === 0"
           class="h-full flex flex-col items-center justify-center text-center px-6 py-12 gap-2">
        <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-1">
          <i class="pi pi-search text-2xl text-gray-400" />
        </div>
        <span class="font-medium">לא נמצאו שירותים</span>
        <span class="text-sm text-gray-500 max-w-[16rem]">לא מצאנו שירות שתואם לחיפוש. נסו שם או קטגוריה אחרים.</span>
      </div>
      <!--     Service List     -->
      <div v-else class="flex flex-col overflow-y-auto mx-4 me-6">
        <TransitionGroup appear name="list" tag="div" class="flex flex-col w-full">
          <div v-for="(service, index) in filteredServices"
               :key="service.id"
               :style="{ transitionDelay: `${index * 50}ms` }"
               @click="selectService(service)"
               :class="['w-full py-4 flex gap-3 justify-start items-center border-gray-200 cursor-pointer', index !== filteredServices.length - 1 ? 'border-b' : '']">
            <Avatar
              v-if="service.imageUrl"
              size="large"
              :image="service.imageUrl"
              class="rounded-2xl overflow-hidden flex-none"/>
            <div class="flex flex-grow justify-between items-center gap-2">
              <div class="flex flex-col gap-1 items-start justify-start">
                <span class="font-medium">{{ service.name }}</span>
                <span class="text-sm">{{ service.description }}</span>
              </div>
              <i :class="['pi', !selectedServices.find(x => x.id === service.id) ? 'pi-circle': 'pi-circle-fill']" />
            </div>
          </div>
        </TransitionGroup>
      </div>
    </div>
    <div class="w-full px-4 flex-shrink-0 shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.05)]">
      <drawer-footer
        approve-label="בחירת שירותים"
        @approve-click="chooseServices"
        @cancel-click="emit('cancel')"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { IService } from '@torim-app/types';
import { useAdminStore } from '@app/stores';
import { useDrawer } from '@torim-app/shared-components';

const props = defineProps<{
  selectedProvider?: string;
  initialServiceIds?: string[];
}>();

const emit = defineEmits<{
  servicesSelected: [services: IService[]]
  cancel: []
}>();

const { closeDrawer } = useDrawer();
const adminStore = useAdminStore();

const services = computed<IService[]>(() =>
  props.selectedProvider ? adminStore.getServices.filter(service => service.providingClients.includes(props.selectedProvider!)) : adminStore.getServices
);

// ── Search + category filter (mirrors the Team services tab) ──
const ALL = '__all__';
const query = ref('');
const cat = ref(ALL);
const categories = computed<string[]>(() =>
  [ALL, ...Array.from(new Set(services.value.map(s => s.category).filter(Boolean) as string[]))]
);
const filteredServices = computed<IService[]>(() => {
  const q = query.value.trim();
  return services.value.filter(s =>
    (cat.value === ALL || s.category === cat.value)
    && (q === '' || s.name.includes(q) || (s.description ?? '').includes(q))
  );
});

const selectedServices = ref<IService[]>(
  props.initialServiceIds
    ? services.value.filter(s => props.initialServiceIds!.includes(s.id))
    : []
);

const selectService = (service: IService) => {
  if (selectedServices.value.find(s => s.id === service.id)) {
    selectedServices.value = selectedServices.value.filter(s => s.id !== service.id);
  } else {
    selectedServices.value.push(service);
  }
};

const chooseServices = () => {
  emit('servicesSelected', selectedServices.value);
  closeDrawer();
};
</script>
